import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/auth.css";
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

// Helper to get CSRF token from cookies
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

// Helper to ensure CSRF cookie is set
async function ensureCSRFToken() {
  // This endpoint reliably sets the CSRF cookie
  await fetch('/api/csrf/', { credentials: 'include' });
}

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Read mode from query string
  function getModeFromQuery() {
    const params = new URLSearchParams(location.search);
    const mode = params.get('mode');
    if (mode === 'login') return false;
    if (mode === 'signup') return true;
    return true; // default to sign up
  }
  const [isSignUp, setIsSignUp] = useState(getModeFromQuery());
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [isJiggling, setIsJiggling] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [error, setError] = useState("");

  // Update isSignUp if query param changes
  useEffect(() => {
    setIsSignUp(getModeFromQuery());
  }, [location.search]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    await ensureCSRFToken(); // Ensure CSRF cookie is set before POST
    const isFormValid = formData.email && formData.password && (isSignUp ? agreeToTerms : true);
    if (!isFormValid) {
      setIsJiggling(true);
      setTimeout(() => setIsJiggling(false), 600);
      return;
    }
    if (isSignUp) {
      // Sign up logic
      try {
        const response = await fetch("/register/", {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "X-CSRFToken": getCookie("csrftoken"),
          },
          body: new URLSearchParams({
            username: formData.email,
            email: formData.email,
            password1: formData.password,
            password2: formData.password,
            consent_status: "true",
          }),
          credentials: "include",
        });
        const result = await response.json();
        if (result.Code === 111) {
          navigate("/onboarding");
        } else if (result.Code === 24 && result.Data && result.Data.length > 0) {
          setError(result.Data[0][1]);
          setIsJiggling(true);
          setTimeout(() => setIsJiggling(false), 600);
        } else {
          setError(result.Message || "Sign up failed. Please try again.");
          setIsJiggling(true);
          setTimeout(() => setIsJiggling(false), 600);
        }
      } catch (err) {
        setError("Network error. Please try again.");
        setIsJiggling(true);
        setTimeout(() => setIsJiggling(false), 600);
      }
    } else {
      // Log in logic
      try {
        const response = await fetch("/loginUser/", {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "X-CSRFToken": getCookie("csrftoken"),
          },
          body: new URLSearchParams({
            username: formData.email,
            password: formData.password,
          }),
          credentials: "include",
        });
        const result = await response.json();
        if (result.Code === 111) {
          if (isSignUp) {
            navigate("/onboarding");
          } else {
            navigate("/");
          }
        } else {
          setError(result.Message || "Login failed. Please try again.");
          setIsJiggling(true);
          setTimeout(() => setIsJiggling(false), 600);
        }
      } catch (err) {
        setError("Network error. Please try again.");
        setIsJiggling(true);
        setTimeout(() => setIsJiggling(false), 600);
      }
    }
  };

  const toggleMode = () => {
    setIsSignUp(!isSignUp);
    setFormData({ email: "", password: "" });
    setAgreeToTerms(false);
    setIsJiggling(false);
    setError("");
  };

  return (
    <div className="auth-container">
      <Header />
      <div className="auth-main text-white bg-white">
        <div className="auth-form-container">
          <h1 className="auth-title">{isSignUp ? "Sign Up" : "Welcome back!"}</h1>
          {error && (
            <div className="error-message" style={{ color: 'red', marginBottom: '1rem', textAlign: 'center' }}>
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email" className="form-label">E-mail*</label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="E-mail address"
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="password" className="form-label">Password*</label>
              <input
                type="password"
                id="password"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                placeholder="Password"
                className="form-input"
              />
            </div>
            {isSignUp && (
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="agree-terms"
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                  className="terms-checkbox"
                />
                <label htmlFor="agree-terms" className="terms-label">
                  I agree to the {" "}
                  <a href="/ToS" className="terms-link" target="_blank" rel="noopener noreferrer">Terms of Service</a>{" "}
                  and {" "}
                  <a href="/privacy" className="terms-link" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                </label>
              </div>
            )}
            <button type="submit" className={`auth-submit-btn ${isJiggling ? "jiggle" : ""}`}>
              {isSignUp ? "Sign Up" : "Log In"}
            </button>
          </form>
          <div className="auth-links">
            {isSignUp ? (
              <p className="auth-switch">
                Already have an account? {" "}
                <button type="button" onClick={toggleMode} className="auth-link-btn">Log In</button>
              </p>
            ) : (
              <>
                {!isSignUp && (
                  <p className="forgot-password">
                    <a href="/password-reset" className="auth-link-btn">Forgot your password?</a>
                  </p>
                )}
                <p className="auth-switch">
                  Need an account? {" "}
                  <button type="button" onClick={toggleMode} className="auth-link-btn">Sign Up</button>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
} 